import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
	FiSliders,
	FiCheckCircle,
	FiMessageCircle,
	FiCheckSquare,
	FiFileText,
	FiSend,
	FiLayers,
	FiArrowLeft,
} from 'react-icons/fi';
import { colorsVariables as colors } from '../GlobalStyles';
import { RootState } from '../store';
import { UserRole } from '../shared/ts/User';
import useCatalog from './useCatalog';
import {
	calculatePricing,
	discountText,
	Service,
	startingPrice,
} from './pricing';
import InquiryForm from './InquiryForm';
import { Action, Check, Columns, Message, Page, Panel } from './styles';
import couple from '../images/couple.jpg';

export default function Services() {
	const { catalog, loading, error, reload } = useCatalog();
	const [selected, setSelected] = useState<number[]>([]);
	const { loggedUser } = useSelector((state: RootState) => state.userLogin);
	const estimate = calculatePricing(catalog.services, selected);

	const toggle = (id: number) =>
		setSelected((ids) =>
			ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
		);

	const depth = (s: Service) => {
		let n = 0;
		let parent = s.parentId;
		const seen = new Set<number>();
		while (parent && !seen.has(parent)) {
			const currentParent = parent;
			seen.add(parent);
			n++;
			parent =
				catalog.services.find((x) => x.id === currentParent)?.parentId || null;
		}

		return Math.min(n, 3);
	};

	const ordered = (services: Service[]) => {
		const result: Service[] = [];
		const visited = new Set<number>();

		const visit = (s: Service) => {
			if (visited.has(s.id)) return;

			visited.add(s.id);
			result.push(s);
			services.filter((x) => x.parentId === s.id).forEach(visit);
		};

		services
			.filter((s) => !services.some((x) => x.id === s.parentId))
			.forEach(visit);
		services.forEach(visit);

		return result;
	};

	return (
		<Page>
			<Hero>
				<div>
					<h1>
						האירוע שלכם,
						<br />
						השירותים שמתאימים לכם
					</h1>
					<p>
						כל זוג מתכנן את היום שלו קצת אחרת. ריכזנו עבורכם את השירותים שיעזרו
						לכם לעשות סדר בתכנון, לדאוג לפרטים ולהגיע לחתונה בראש שקט יותר. בחרו
						את מה שמתאים לכם, קבלו הערכת מחיר ראשונית, ונמשיך יחד בשיחה כדי
						לדייק את הפרטים.
					</p>
					<Action
						onClick={() =>
							document
								.getElementById('selector')
								?.scrollIntoView({ behavior: 'smooth' })
						}
					>
						בואו נבחר את השירותים שלכם
					</Action>
				</div>
				<img src={couple} alt="זוג ביום החתונה" />
			</Hero>
			<Benefits>
				<BenefitCard>
					<IconTile aria-hidden="true">
						<FiSliders />
					</IconTile>
					<div>
						<h3>בוחרים לפי הצורך שלכם</h3>
						<p>מרכיבים את השירותים שמתאימים לאירוע ולסדרי העדיפויות שלכם.</p>
					</div>
				</BenefitCard>
				<BenefitCard>
					<IconTile aria-hidden="true">
						<FiCheckCircle />
					</IconTile>
					<div>
						<h3>רואים מה כלול</h3>
						<p>מבינים אילו שירותים נכללים בבחירה ואילו הנחות חלות עליה.</p>
					</div>
				</BenefitCard>
				<BenefitCard>
					<IconTile aria-hidden="true">
						<FiMessageCircle />
					</IconTile>
					<div>
						<h3>מגיעים לשיחה מוכנים יותר</h3>
						<p>שולחים את הבחירה שלכם וממשיכים להתאמה אישית עם הצוות.</p>
					</div>
				</BenefitCard>
			</Benefits>
			<Process aria-label="איך זה עובד">
				<ProcessStep>
					<FiCheckSquare aria-hidden="true" />
					<span>בוחרים שירותים</span>
				</ProcessStep>
				<ProcessArrow aria-hidden="true">
					<FiArrowLeft />
				</ProcessArrow>
				<ProcessStep>
					<FiFileText aria-hidden="true" />
					<span>רואים הערכת מחיר</span>
				</ProcessStep>
				<ProcessArrow aria-hidden="true">
					<FiArrowLeft />
				</ProcessArrow>
				<ProcessStep>
					<FiSend aria-hidden="true" />
					<span>שולחים ונמשיך יחד</span>
				</ProcessStep>
			</Process>
			{loggedUser?.role === UserRole.ADMIN && (
				<p>
					<Link to="/admin/services">ערוך שירותים</Link>
				</p>
			)}
			<section id="selector">
				<h2>השירותים שלנו</h2>
				<p>
					כל המחירים הם מחירי התחלה. ההצעה הסופית תלויה בפרטי האירוע, כולל מספר
					האורחים ככל שרלוונטי. את הפרטים נדייק בשיחת ההמשך.
				</p>
				{loading ? (
					<p role="status">טוענים שירותים…</p>
				) : error ? (
					<Message role="alert">
						{error} <Action onClick={reload}>נסו שוב</Action>
					</Message>
				) : !catalog.services.length ? (
					<Panel>השירותים והמחירים יתעדכנו כאן בקרוב.</Panel>
				) : (
					<Columns>
						<div>
							{catalog.categories
								.filter((c) =>
									catalog.services.some((s) => s.categoryId === c.id)
								)
								.map((category) => (
									<Panel key={category.id}>
										<h2>{category.name}</h2>
										{ordered(
											catalog.services.filter(
												(s) => s.categoryId === category.id
											)
										).map((service) => {
											const row = estimate.rows.find(
												(r) => r.service.id === service.id
											);
											const included = !!row?.includers.length;

											return (
												<ServiceCard
													key={service.id}
													$active={!!row}
													style={{ marginInlineStart: depth(service) * 16 }}
												>
													<Check>
														<input
															type="checkbox"
															checked={!!row}
															disabled={included}
															onChange={() => toggle(service.id)}
															aria-describedby={`details-${service.id}`}
														/>
														<ServiceIcon aria-hidden="true">
															{included ? <FiCheckCircle /> : <FiLayers />}
														</ServiceIcon>
														<span>
															<strong>{service.name}</strong>
															<br />
															{startingPrice(service.priceCents)}
														</span>
													</Check>
													<div id={`details-${service.id}`}>
														<p>{service.description}</p>
														{service.parentId && (
															<p>
																שירות המשך של{' '}
																{
																	catalog.services.find(
																		(s) => s.id === service.parentId
																	)?.name
																}{' '}
																- ניתן לבחור בנפרד.
															</p>
														)}
														{!!service.includedIds.length && (
															<p>
																כולל במחיר:{' '}
																{service.includedIds
																	.map(
																		(id) =>
																			catalog.services.find((s) => s.id === id)
																				?.name
																	)
																	.join(', ')}
															</p>
														)}
														{service.discounts.map((d, i) => (
															<p key={i}>
																בחירת השירות מפעילה הנחה של {discountText(d)} על{' '}
																{d.allServices
																	? 'כל השירותים'
																	: d.targetIds
																			.map(
																				(id) =>
																					catalog.services.find(
																						(s) => s.id === id
																					)?.name
																			)
																			.join(', ')}
																. חלה הנחה אחת לכל שירות; הנחה ייעודית קודמת
																לכללית.
															</p>
														))}
														{included && (
															<p>
																<strong>כלול במחיר</strong> במסגרת{' '}
																{row!.includers.join(', ')}. כדי להסירו יש להסיר
																תחילה את השירותים הכוללים אותו.
																{row!.explicit &&
																	' שירות זה נבחר גם במפורש ויישאר בבחירה לאחר הסרתם.'}
															</p>
														)}
													</div>
												</ServiceCard>
											);
										})}
									</Panel>
								))}
						</div>
						<Summary>
							<Panel>
								<SummaryHeading>
									<FiFileText aria-hidden="true" />
									הבחירה שלכם
								</SummaryHeading>
								<div aria-live="polite">
									{!estimate.rows.length && (
										<p>בחרו את השירותים שמתאימים לכם וההערכה תופיע כאן.</p>
									)}
									{estimate.rows.map((r) => (
										<ServiceRow key={r.service.id}>
											<strong>{r.service.name}</strong>
											<p>{r.explicit ? 'נבחר במפורש' : 'נכלל אוטומטית'}</p>
											{r.includers.length ? (
												<p>כלול במחיר · {r.includers.join(', ')}</p>
											) : (
												<>
													<p>
														מחיר התחלתי: {startingPrice(r.service.priceCents)}
													</p>
													{r.discount && (
														<p>
															הנחה של {discountText(r.discount)} בעקבות{' '}
															{r.discount.ownerName}
														</p>
													)}
													<p>
														<strong>{startingPrice(r.priceCents)}</strong>
													</p>
												</>
											)}
											{r.explicit && !r.includers.length && (
												<button onClick={() => toggle(r.service.id)}>
													הסרה
												</button>
											)}
										</ServiceRow>
									))}
									<Total>
										<span>סך ההערכה</span>
										<h3>{startingPrice(estimate.totalCents)}</h3>
									</Total>
								</div>
								<a href="#inquiry">ממשיכים להצעה מותאמת</a>
							</Panel>
						</Summary>
					</Columns>
				)}
			</section>
			<InquiryForm estimate={estimate} />
			{!!estimate.rows.length && (
				<MobileBar>
					<span>{startingPrice(estimate.totalCents)}</span>
					<a href="#inquiry">ממשיכים להצעה</a>
				</MobileBar>
			)}
		</Page>
	);
}

const Hero = styled.section`
	display: grid;
	grid-template-columns: 1.1fr 1fr;
	gap: 32px;
	align-items: center;
	padding: 24px 0 48px;
	img {
		width: 100%;
		height: 420px;
		object-fit: cover;
	}
	@media (max-width: 650px) {
		grid-template-columns: 1fr;
		gap: 16px;
		padding-bottom: 24px;
		img {
			height: 190px;
		}
	}
`;

const Benefits = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px;
	padding: 16px 0 28px;

	@media (max-width: 650px) {
		grid-template-columns: 1fr;
		gap: 12px;
	}
`;

const BenefitCard = styled.article`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;
	padding: 28px;
	background: #f8f5eb;
	border-radius: 12px;

	h3 {
		margin-bottom: 10px;
		line-height: 1.4;
	}
	p {
		margin: 0;
	}

	@media (max-width: 650px) {
		flex-direction: row;
		gap: 16px;
		padding: 20px;
		h3 {
			font-size: 18px;
			margin-bottom: 6px;
		}
	}
`;

const IconTile = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 52px;
	flex-shrink: 0;
	background: ${colors.secDark};
	color: ${colors.mainDark};
	border-radius: 12px;
	font-size: 25px;

	@media (max-width: 650px) {
		width: 44px;
		height: 44px;
		font-size: 22px;
	}
`;

const Process = styled.div`
	display: grid;
	grid-template-columns: 1fr auto 1fr auto 1fr;
	align-items: center;
	gap: 16px;
	padding: 28px 32px;
	margin-bottom: 48px;
	background: ${colors.secDark};
	color: ${colors.white};
	border-radius: 12px;

	@media (max-width: 650px) {
		gap: 8px;
		padding: 24px 12px;
		margin-bottom: 32px;
	}
`;

const ProcessStep = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	font-weight: 500;
	svg {
		flex-shrink: 0;
		width: 28px;
		height: 28px;
		color: ${colors.mainDark};
	}

	@media (max-width: 850px) {
		flex-direction: column;
		gap: 10px;
		text-align: center;
	}
	@media (max-width: 650px) {
		font-size: 14px;
		line-height: 1.5;
	}
`;

const ProcessArrow = styled.span`
	display: flex;
	color: ${colors.mainDark};
	font-size: 22px;

	@media (max-width: 650px) {
		font-size: 16px;
	}
`;

const ServiceRow = styled.div`
	padding: 20px 0;
	overflow-wrap: anywhere;
	& + & {
		margin-top: 16px;
	}
	button {
		text-decoration: underline;
		background: transparent;
		min-height: 44px;
	}
`;

const ServiceCard = styled(ServiceRow)<{ $active: boolean }>`
	padding: 24px;
	background: ${({ $active }) => ($active ? '#fcf8e9' : colors.white)};
	border: 2px solid
		${({ $active }) => ($active ? colors.mainDark : 'transparent')};
	border-radius: 12px;
	transition: border-color 0.15s ease, background-color 0.15s ease;

	&:focus-within {
		border-color: ${colors.mainDark};
	}
	&:hover {
		border-color: ${colors.mainDark};
	}
	${Check} {
		align-items: center;
	}
	${Check} input {
		margin-top: 0;
	}

	@media (max-width: 650px) {
		padding: 18px 14px;
	}
	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const ServiceIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	background: #f1ead4;
	color: ${colors.secDark};
	border-radius: 8px;
	font-size: 22px;
`;

const SummaryHeading = styled.h2`
	display: flex;
	align-items: center;
	gap: 12px;
	svg {
		color: #8b6c17;
		flex-shrink: 0;
	}
`;

const Total = styled.div`
	background: #eee4c6;
	padding: 20px;
	margin: 20px 0;
	border-radius: 8px;
	span {
		display: block;
		margin-bottom: 4px;
	}
`;

const Summary = styled.aside`
	position: sticky;
	top: 110px;
	max-height: calc(100vh - 130px);
	overflow-y: auto;
	@media (max-width: 850px) {
		position: static;
		max-height: none;
	}
`;

const MobileBar = styled.div`
	display: none;
	@media (max-width: 850px) {
		display: flex;
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		background: #000;
		color: #d4af37;
		padding: 14px 20px;
		justify-content: space-between;
		gap: 12px;
		z-index: 6;
	}
`;
